import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DataHistogramIcon from "@icons/DataHistogramIcon";
import { compareCfrSectionIds } from "@utils/cfrSectionSort";
import {
  FacilityComplianceData,
  FederalComplianceFacilityHint,
  FederalCompliancePayload,
  deriveFederalComplianceFacilityFlags,
  facilityComplianceService,
  getPendingItemDisplayText,
} from "@services/facilityCompliance";
import { formatDate, formatCurrencyAmount } from "@utils/formatUtils";
import { auditLogService } from "@services/auditLog";
import { getCurrentUserId } from "@utils/userUtils";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { GlobalAdminLlmPromptButton } from "@components/Spaces/GlobalAdminLlmPromptButton";
import { AuditChecklistTable } from "@components/Spaces/AuditChecklistTable";
import { LicensingRequirementsTable } from "@components/Spaces/LicensingRequirementsTable";

const ComplianceHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  gap: "8px",
  padding: "4px",
  background: "#fff",
  alignSelf: "stretch",
  width: "100%",
});

const StyledAccordion = styled(Accordion)({
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:before": {
    display: "none",
  },
  "& .accordion-title": {
    color: "#262626",
    transition: "color 0.3s ease",
  },
  "&.Mui-expanded .accordion-title": {
    color: "#065B70",
  },
});

const AccordionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
});

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M15 12.5854C15.5826 12.7913 16 13.3469 16 14V14.5C16 15.6046 15.1046 16.5 14 16.5H6C4.89543 16.5 4 15.6046 4 14.5V9C4 8.34689 4.4174 7.79127 5 7.58535V6C5 5.17157 5.67157 4.5 6.5 4.5H11.5C12.3284 4.5 13 5.17157 13 6V12.5H14L13.9997 6.58525C14.5825 6.79109 15 7.34678 15 8V12.5854ZM12 11.7929V6C12 5.72386 11.7761 5.5 11.5 5.5H6.5C6.22386 5.5 6 5.72386 6 6V7.5H7.08579C7.48361 7.5 7.86514 7.65804 8.14645 7.93934L12 11.7929ZM5 9V14.5C5 15.0523 5.44772 15.5 6 15.5H14C14.5523 15.5 15 15.0523 15 14.5V14C15 13.7239 14.7761 13.5 14.5 13.5H12.9142C12.5164 13.5 12.1349 13.342 11.8536 13.0607L7.43934 8.64645C7.34557 8.55268 7.21839 8.5 7.08579 8.5H5.5C5.22386 8.5 5 8.72386 5 9Z"
      fill="#065B70"
    />
  </svg>
);
const LicencingIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M16.5625 2.50077L16.5 2.5H6.5C5.11929 2.5 4 3.61929 4 5V13.5H2C1.44772 13.5 1 13.9477 1 14.5V16.1875C1 17.5211 2.0858 18.5 3.375 18.5H12.5156C13.8787 18.4917 14.9831 17.3924 14.9998 16.0313L15 16V7.5H18C18.5523 7.5 19 7.05229 19 6.5V5C19 3.64018 17.9143 2.53394 16.5625 2.50077ZM6.5 3.5H14.4998C14.2093 3.88671 14.0284 4.3604 14.0031 4.875H14V16C14 16.8284 13.3284 17.5 12.5 17.5C11.6716 17.5 11 16.8284 11 16V14.5C11 13.9477 10.5523 13.5 10 13.5H5V5C5 4.17157 5.67157 3.5 6.5 3.5ZM16.4988 3.5H16.5012C17.3291 3.50065 18 4.17198 18 5V6.5H15V5C15 4.17198 15.6709 3.50065 16.4988 3.5ZM2 14.5H10V16C10 16.5628 10.186 17.0822 10.4998 17.5H3.375C2.59313 17.5 2 16.9249 2 16.1875V14.5Z"
      fill="#065B70"
    />
  </svg>
);
const AccreditationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M4 3.5C2.89543 3.5 2 4.39543 2 5.5V9.17133C2.28512 8.81899 2.62236 8.51056 3 8.25777V5.5C3 4.94772 3.44772 4.5 4 4.5H16C16.5523 4.5 17 4.94772 17 5.5V13.5C17 14.0523 16.5523 14.5 16 14.5H9.24223C9.16639 14.6133 9.08554 14.723 9 14.8287V15.5H16C17.1046 15.5 18 14.6046 18 13.5V5.5C18 4.39543 17.1046 3.5 16 3.5H4ZM5 7C5 6.72386 5.22386 6.5 5.5 6.5H14.5C14.7761 6.5 15 6.72386 15 7C15 7.27614 14.7761 7.5 14.5 7.5H5.5C5.22386 7.5 5 7.27614 5 7ZM5.5 15.5C3.567 15.5 2 13.933 2 12C2 10.067 3.567 8.5 5.5 8.5C7.433 8.5 9 10.067 9 12C9 13.933 7.433 15.5 5.5 15.5ZM3 15.7422C3.71505 16.2209 4.57493 16.5 5.5 16.5C6.42507 16.5 7.28495 16.2209 8 15.7422V18.5C8 18.912 7.52962 19.1472 7.2 18.9L5.8 17.85C5.62222 17.7167 5.37778 17.7167 5.2 17.85L3.8 18.9C3.47038 19.1472 3 18.912 3 18.5V15.7422ZM10.5 10.5C10.2239 10.5 10 10.7239 10 11C10 11.2761 10.2239 11.5 10.5 11.5H14.5C14.7761 11.5 15 11.2761 15 11C15 10.7239 14.7761 10.5 14.5 10.5H10.5Z"
      fill="#065B70"
    />
  </svg>
);
const ComplianceIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M3 3.75C3 3.61193 3.11193 3.5 3.25 3.5H8.75C8.88807 3.5 9 3.61193 9 3.75V6.25C9 6.94036 9.55964 7.5 10.25 7.5H11.75C11.8881 7.5 12 7.61193 12 7.75V8.5H13V7.75C13 7.05964 12.4404 6.5 11.75 6.5H10.25C10.1119 6.5 10 6.38807 10 6.25V3.75C10 3.05964 9.44036 2.5 8.75 2.5H3.25C2.55964 2.5 2 3.05964 2 3.75V14.25C2 14.9404 2.55964 15.5 3.25 15.5H7V14.5H3.25C3.11193 14.5 3 14.3881 3 14.25V3.75ZM7.25 9C7.52312 9 7.76213 9.14598 7.89328 9.36418C7.57415 9.679 7.32522 10.0648 7.17212 10.496C6.7945 10.457 6.5 10.1379 6.5 9.75C6.5 9.33579 6.83579 9 7.25 9ZM4.75 6C5.16421 6 5.5 5.66421 5.5 5.25C5.5 4.83579 5.16421 4.5 4.75 4.5C4.33579 4.5 4 4.83579 4 5.25C4 5.66421 4.33579 6 4.75 6ZM5.5 7.5C5.5 7.91421 5.16421 8.25 4.75 8.25C4.33579 8.25 4 7.91421 4 7.5C4 7.08579 4.33579 6.75 4.75 6.75C5.16421 6.75 5.5 7.08579 5.5 7.5ZM4.75 10.5C5.16421 10.5 5.5 10.1642 5.5 9.75C5.5 9.33579 5.16421 9 4.75 9C4.33579 9 4 9.33579 4 9.75C4 10.1642 4.33579 10.5 4.75 10.5ZM8 5.25C8 5.66421 7.66421 6 7.25 6C6.83579 6 6.5 5.66421 6.5 5.25C6.5 4.83579 6.83579 4.5 7.25 4.5C7.66421 4.5 8 4.83579 8 5.25ZM7.25 8.25C7.66421 8.25 8 7.91421 8 7.5C8 7.08579 7.66421 6.75 7.25 6.75C6.83579 6.75 6.5 7.08579 6.5 7.5C6.5 7.91421 6.83579 8.25 7.25 8.25ZM8 11.5C8 10.3954 8.89543 9.5 10 9.5H17C18.1046 9.5 19 10.3954 19 11.5V15.5C19 16.6046 18.1046 17.5 17 17.5H15V18.5H16.5C16.7761 18.5 17 18.7239 17 19C17 19.2761 16.7761 19.5 16.5 19.5H10.5C10.2239 19.5 10 19.2761 10 19C10 18.7239 10.2239 18.5 10.5 18.5H12V17.5H10C8.89543 17.5 8 16.6046 8 15.5V11.5ZM10 16.5H17C17.5523 16.5 18 16.0523 18 15.5V11.5C18 10.9477 17.5523 10.5 17 10.5H10C9.44772 10.5 9 10.9477 9 11.5V15.5C9 16.0523 9.44772 16.5 10 16.5ZM14 18.5V17.5H13V18.5H14Z"
      fill="#065B70"
    />
  </svg>
);
const RequirementsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M3 3.75C3 3.61193 3.11193 3.5 3.25 3.5H8.75C8.88807 3.5 9 3.61193 9 3.75V6.25C9 6.94036 9.55964 7.5 10.25 7.5H11.75C11.8881 7.5 12 7.61193 12 7.75V8.5H13V7.75C13 7.05964 12.4404 6.5 11.75 6.5H10.25C10.1119 6.5 10 6.38807 10 6.25V3.75C10 3.05964 9.44036 2.5 8.75 2.5H3.25C2.55964 2.5 2 3.05964 2 3.75V14.25C2 14.9404 2.55964 15.5 3.25 15.5H7V14.5H3.25C3.11193 14.5 3 14.3881 3 14.25V3.75ZM7.25 9C7.52312 9 7.76213 9.14598 7.89328 9.36418C7.57415 9.679 7.32522 10.0648 7.17212 10.496C6.7945 10.457 6.5 10.1379 6.5 9.75C6.5 9.33579 6.83579 9 7.25 9ZM4.75 6C5.16421 6 5.5 5.66421 5.5 5.25C5.5 4.83579 5.16421 4.5 4.75 4.5C4.33579 4.5 4 4.83579 4 5.25C4 5.66421 4.33579 6 4.75 6ZM5.5 7.5C5.5 7.91421 5.16421 8.25 4.75 8.25C4.33579 8.25 4 7.91421 4 7.5C4 7.08579 4.33579 6.75 4.75 6.75C5.16421 6.75 5.5 7.08579 5.5 7.5ZM4.75 10.5C5.16421 10.5 5.5 10.1642 5.5 9.75C5.5 9.33579 5.16421 9 4.75 9C4.33579 9 4 9.33579 4 9.75C4 10.1642 4.33579 10.5 4.75 10.5ZM8 5.25C8 5.66421 7.66421 6 7.25 6C6.83579 6 6.5 5.66421 6.5 5.25C6.5 4.83579 6.83579 4.5 7.25 4.5C7.66421 4.5 8 4.83579 8 5.25ZM7.25 8.25C7.66421 8.25 8 7.91421 8 7.5C8 7.08579 7.66421 6.75 7.25 6.75C6.83579 6.75 6.5 7.08579 6.5 7.5C6.5 7.91421 6.83579 8.25 7.25 8.25ZM8 11.5C8 10.3954 8.89543 9.5 10 9.5H17C18.1046 9.5 19 10.3954 19 11.5V15.5C19 16.6046 18.1046 17.5 17 17.5H15V18.5H16.5C16.7761 18.5 17 18.7239 17 19C17 19.2761 16.7761 19.5 16.5 19.5H10.5C10.2239 19.5 10 19.2761 10 19C10 18.7239 10.2239 18.5 10.5 18.5H12V17.5H10C8.89543 17.5 8 16.6046 8 15.5V11.5ZM10 16.5H17C17.5523 16.5 18 16.0523 18 15.5V11.5C18 10.9477 17.5523 10.5 17 10.5H10C9.44772 10.5 9 10.9477 9 11.5V15.5C9 16.0523 9.44772 16.5 10 16.5ZM14 18.5V17.5H13V18.5H14Z"
      fill="#065B70"
    />
  </svg>
);
const CheckListIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M5.85355 4.85355C6.04882 4.65829 6.04882 4.34171 5.85355 4.14645C5.65829 3.95118 5.34171 3.95118 5.14645 4.14645L3.5 5.79289L2.85355 5.14645C2.65829 4.95118 2.34171 4.95118 2.14645 5.14645C1.95118 5.34171 1.95118 5.65829 2.14645 5.85355L3.14645 6.85355C3.34171 7.04882 3.65829 7.04882 3.85355 6.85355L5.85355 4.85355ZM8.5 5.5C8.22386 5.5 8 5.72386 8 6C8 6.27614 8.22386 6.5 8.5 6.5H17.5C17.7761 6.5 18 6.27614 18 6C18 5.72386 17.7761 5.5 17.5 5.5H8.5ZM8.5 10.5C8.22386 10.5 8 10.7239 8 11C8 11.2761 8.22386 11.5 8.5 11.5H17.5C17.7761 11.5 18 11.2761 18 11C18 10.7239 17.7761 10.5 17.5 10.5H8.5ZM8 16C8 15.7239 8.22386 15.5 8.5 15.5H17.5C17.7761 15.5 18 15.7239 18 16C18 16.2761 17.7761 16.5 17.5 16.5H8.5C8.22386 16.5 8 16.2761 8 16ZM5.85355 10.3536C6.04882 10.1583 6.04882 9.84171 5.85355 9.64645C5.65829 9.45118 5.34171 9.45118 5.14645 9.64645L3.5 11.2929L2.85355 10.6464C2.65829 10.4512 2.34171 10.4512 2.14645 10.6464C1.95118 10.8417 1.95118 11.1583 2.14645 11.3536L3.14645 12.3536C3.34171 12.5488 3.65829 12.5488 3.85355 12.3536L5.85355 10.3536ZM5.85355 14.6464C6.04882 14.8417 6.04882 15.1583 5.85355 15.3536L3.85355 17.3536C3.65829 17.5488 3.34171 17.5488 3.14645 17.3536L2.14645 16.3536C1.95118 16.1583 1.95118 15.8417 2.14645 15.6464C2.34171 15.4512 2.65829 15.4512 2.85355 15.6464L3.5 16.2929L5.14645 14.6464C5.34171 14.4512 5.65829 14.4512 5.85355 14.6464Z"
      fill="#065B70"
    />
  </svg>
);
const CfrRegulationsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M4 3.5C2.89543 3.5 2 4.39543 2 5.5V15.5C2 16.6046 2.89543 17.5 4 17.5H16C17.1046 17.5 18 16.6046 18 15.5V5.5C18 4.39543 17.1046 3.5 16 3.5H4ZM4 5.5H16V15.5H4V5.5ZM6 7.5C5.44772 7.5 5 7.94772 5 8.5C5 9.05228 5.44772 9.5 6 9.5H14C14.5523 9.5 15 9.05228 15 8.5C15 7.94772 14.5523 7.5 14 7.5H6ZM6 11.5C5.44772 11.5 5 11.9477 5 12.5C5 13.0523 5.44772 13.5 6 13.5H10C10.5523 13.5 11 13.0523 11 12.5C11 11.9477 10.5523 11.5 10 11.5H6Z"
      fill="#065B70"
    />
  </svg>
);
// Interface is now imported from service

interface Facility extends FederalComplianceFacilityHint {
  facility_id: number;
  facility_name: string;
  city: string;
  state: string;
  full_address: string;
  facility_type: string;
  /** ISO-style code from API (e.g. CAN, USA) — used for country-aware compliance copy */
  country_code?: string | null;
  vertical?: string | null;
  /** True after the user accepts the facility profile (server + session stamp). */
  is_profile_approved?: boolean;
}

interface FacilityAnalysisProps {
  complianceData: FacilityComplianceData | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  selectedFacility?: Facility | null;
  /** When true, Hub user has the Facility Analysis tab selected — start federal `/api/v1/facilities/` prefetch without waiting for parallel compliance. */
  analysisTabActive?: boolean;
  onWatchGuideClick?: () => void;
  watchGuideLoading?: boolean;
  watchGuideIconSrc?: string;
  showWatchGuide?: boolean;
}

function isHealthcareFacilityContext(
  facility: Facility | null | undefined,
  complianceData: FacilityComplianceData | null,
): boolean {
  const vertical = String(
    facility?.vertical ?? (complianceData?.entityDetails as any)?.vertical ?? "",
  )
    .trim()
    .toLowerCase();
  if (vertical) {
    return vertical === "healthcare" || vertical === "health_care";
  }

  const profileText = [
    facility?.primary_profile_slug,
    facility?.facility_type,
    complianceData?.entityDetails?.facility_type,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    profileText.includes("food_service") ||
    profileText.includes("food service") ||
    profileText.includes("bar / tavern") ||
    profileText.includes("aviation_") ||
    profileText.includes("aviation")
  ) {
    return false;
  }

  return true;
}

function hasMeaningfulAnalysisValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") {
    return Object.values(value).some(hasMeaningfulAnalysisValue);
  }
  return value !== null && value !== undefined && value !== "";
}

function isCanadaCountryCode(code: string | null | undefined): boolean {
  const u = String(code ?? "")
    .trim()
    .toUpperCase();
  return u === "CAN" || u === "CA" || u === "CANADA";
}

const FEDERAL_COMPLIANCE_STRUCTURE_KEYS = new Set(["CMS", "HIPAA", "OSHA"]);

function formatFederalComplianceKeyLabel(key: string): string {
  if (key === "OtherFederal") {
    return "Other national requirements";
  }
  const spaced = key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
  if (!spaced) {
    return spaced;
  }
  // CamelCase keys often start with a lowercase segment (e.g. mandatoryHospitalReporting).
  const first = spaced.charAt(0);
  if (/[a-z]/.test(first)) {
    return first.toUpperCase() + spaced.slice(1);
  }
  return spaced;
}

function renderFederalComplianceDynamicSections(
  federalCompliance: Record<string, unknown>,
): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  for (const [agencyKey, raw] of Object.entries(federalCompliance)) {
    if (FEDERAL_COMPLIANCE_STRUCTURE_KEYS.has(agencyKey)) {
      continue;
    }
    if (raw == null) {
      continue;
    }
    if (Array.isArray(raw)) {
      const items = raw.filter(
        (v): v is { text: string; citation?: { url?: string; title?: string } } =>
          typeof v === "object" &&
          v !== null &&
          typeof (v as { text?: unknown }).text === "string",
      );
      if (items.length === 0) {
        continue;
      }
      nodes.push(
        <Box
          key={agencyKey}
          sx={{
            backgroundColor: "#f8fdfe",
            padding: "8px",
            borderRadius: "8px",
            border: "1px solid #cff7ff",
            marginBottom: "8px",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#065b70",
              fontFamily: "Rethink Sans, sans-serif",
              marginBottom: "8px",
            }}
          >
            {formatFederalComplianceKeyLabel(agencyKey)}
          </Typography>
          <Box sx={{ paddingLeft: "8px" }}>
            {items.map((item, index) => (
              <Typography
                key={index}
                sx={{
                  fontSize: "14px",
                  color: "#065b70",
                  lineHeight: 1.6,
                  fontFamily: "Rethink Sans, sans-serif",
                  marginBottom: "8px",
                }}
              >
                • {item.text}
                {item.citation && (
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      fontSize: "12px",
                      color: "#737373",
                      marginLeft: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {item.citation.url ? (
                      <a
                        href={item.citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#0a829d",
                          textDecoration: "underline",
                        }}
                      >
                        {item.citation.title}
                      </a>
                    ) : (
                      item.citation.title
                    )}
                  </Box>
                )}
              </Typography>
            ))}
          </Box>
        </Box>,
      );
      continue;
    }
    if (typeof raw === "object") {
      const obj = raw as Record<string, unknown>;
      const subKeys = Object.keys(obj).filter((sk) => {
        const val = obj[sk];
        return (
          Array.isArray(val) &&
          val.length > 0 &&
          val.every(
            (v) =>
              typeof v === "object" &&
              v !== null &&
              typeof (v as { text?: unknown }).text === "string",
          )
        );
      });
      if (subKeys.length === 0) {
        continue;
      }
      nodes.push(
        <Box
          key={agencyKey}
          sx={{
            backgroundColor: "#f8fdfe",
            padding: "8px",
            borderRadius: "8px",
            border: "1px solid #cff7ff",
            marginBottom: "8px",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#065b70",
              fontFamily: "Rethink Sans, sans-serif",
              marginBottom: "8px",
            }}
          >
            {formatFederalComplianceKeyLabel(agencyKey)}
          </Typography>
          {subKeys.map((sk) => (
            <Box
              key={`${agencyKey}-${sk}`}
              sx={{
                backgroundColor: "#f8fdfe",
                padding: "8px",
                borderRadius: "8px",
                border: "1px solid #5bbfd6",
                marginBottom: "8px",
              }}
            >
              <Box
                sx={{
                  borderBottom: "1px solid #3095ab",
                  paddingBottom: "8px",
                  marginBottom: "8px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#065b70",
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  {formatFederalComplianceKeyLabel(sk)}
                </Typography>
              </Box>
              <Box sx={{ paddingLeft: "8px" }}>
                {(obj[sk] as { text: string; citation?: { url?: string; title?: string } }[]).map(
                  (item, index) => (
                    <Typography
                      key={index}
                      sx={{
                        fontSize: "14px",
                        color: "#065b70",
                        lineHeight: 1.6,
                        fontFamily: "Rethink Sans, sans-serif",
                        marginBottom: "8px",
                      }}
                    >
                      • {item.text}
                      {item.citation && (
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            fontSize: "12px",
                            color: "#737373",
                            marginLeft: "12px",
                            marginTop: "4px",
                          }}
                        >
                          {item.citation.url ? (
                            <a
                              href={item.citation.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#0a829d",
                                textDecoration: "underline",
                              }}
                            >
                              {item.citation.title}
                            </a>
                          ) : (
                            item.citation.title
                          )}
                        </Box>
                      )}
                    </Typography>
                  ),
                )}
              </Box>
            </Box>
          ))}
        </Box>,
      );
    }
  }
  return <>{nodes}</>;
}

const FacilityAnalysis: React.FC<FacilityAnalysisProps> = ({
  complianceData,
  loading = false,
  error = null,
  onRetry,
  selectedFacility = null,
  analysisTabActive = false,
  onWatchGuideClick,
  watchGuideLoading = false,
  watchGuideIconSrc,
  showWatchGuide = true,
}) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [expandedCfrPart, setExpandedCfrPart] = useState<string | false>(false);
  const [expandedSourceDocument, setExpandedSourceDocument] =
    useState<string | false>(false);
  const [federalComplianceData, setFederalComplianceData] = useState<any>(null);
  const [federalComplianceLoading, setFederalComplianceLoading] =
    useState(false);
  const [federalComplianceError, setFederalComplianceError] = useState<
    string | null
  >(null);
  const federalComplianceInFlightRef = useRef(false);
  /** Bumped only when the selected facility id actually changes — invalidates stale /facilities/ fetches (LLM can retry for minutes). */
  const federalComplianceOpIdRef = useRef(0);
  const federalComplianceAbortRef = useRef<AbortController | null>(null);
  /** Prevents prefetch effect from scheduling duplicate POSTs for the same facility (effect dep churn). */
  const federalPrefetchScheduledForFacilityRef = useRef<number | null>(null);
  const complianceDataRef = useRef(complianceData);
  const prevFederalFacilityIdRef = useRef<number | undefined>(undefined);
  /**
   * Client cap so the accordion cannot spin indefinitely. Must be ≥ server
   * `FACILITIES_LLM_INVOKE_TIMEOUT_SECONDS` (default 120s) plus network margin so we do not
   * abort while the API is still working (LangChain can otherwise retry for many minutes).
   */
  const _facServerDeadlineSecRaw = Number(
    process.env.REACT_APP_FACILITIES_LLM_INVOKE_TIMEOUT_SECONDS || "120",
  );
  const _facServerDeadlineSec =
    Number.isFinite(_facServerDeadlineSecRaw) && _facServerDeadlineSecRaw > 0
      ? _facServerDeadlineSecRaw
      : 120;
  /** Align with rules API ``FACILITIES_LLM_INVOKE_TIMEOUT_SECONDS`` + network buffer (seconds → ms). */
  const FEDERAL_COMPLIANCE_FETCH_TIMEOUT_MS = Math.min(
    600_000,
    Math.round((_facServerDeadlineSec + 45) * 1000),
  );
  /** Avoid skipping prefetch while `federalComplianceData` is still stale from the previous facility in the same effect flush. */
  const federalComplianceLoadedForFacilityIdRef = useRef<number | null>(null);
  const selectedFacilityRef = useRef(selectedFacility);
  const effectiveFacility =
    selectedFacility ?? getSelectedFacilityFromSession<Facility>();
  selectedFacilityRef.current = effectiveFacility;
  const isHealthcareFacility = isHealthcareFacilityContext(
    effectiveFacility,
    complianceData,
  );

  complianceDataRef.current = complianceData;

  // Clear federal compliance data when facility changes; abort and unlock so a prior
  // facility's long-running POST /api/v1/facilities/ cannot block the next facility's fetch.
  useEffect(() => {
    const id = selectedFacility?.facility_id;
    if (prevFederalFacilityIdRef.current === id) {
      return;
    }
    prevFederalFacilityIdRef.current = id;

    federalComplianceAbortRef.current?.abort();
    federalComplianceAbortRef.current = null;
    federalComplianceOpIdRef.current += 1;
    setFederalComplianceData(null);
    setFederalComplianceError(null);
    setExpanded(false);
    federalComplianceLoadedForFacilityIdRef.current = null;
    federalComplianceInFlightRef.current = false;
    federalPrefetchScheduledForFacilityRef.current = null;
    setFederalComplianceLoading(false);
  }, [selectedFacility?.facility_id]);

  // Prefetch federal compliance (POST /api/v1/facilities/, Redis key facilities:* on server — not populated by parallel compliance):
  // - After facility profile is approved (avoids LLM contention with profile bootstrap / parallel compliance).
  // - When parallel compliance has returned (complianceData), even if another Hub tab is visible (this component stays mounted).
  // - When the Facility Analysis tab is active, even before complianceData — runs in parallel with parallel compliance for faster first paint.
  useEffect(() => {
    if (!complianceDataRef.current && !analysisTabActive) return;
    if (!isHealthcareFacility) return;
    const facility =
      selectedFacility ?? getSelectedFacilityFromSession<Facility>();
    const facilityId = facility?.facility_id;
    if (!facilityId) return;
    if (facility.is_profile_approved !== true) {
      return;
    }
    if (federalComplianceLoadedForFacilityIdRef.current === facilityId) {
      return;
    }
    if (federalPrefetchScheduledForFacilityRef.current === facilityId) {
      return;
    }
    if (federalComplianceInFlightRef.current) {
      return;
    }
    federalPrefetchScheduledForFacilityRef.current = facilityId;
    void fetchFederalCompliance();
    // Do not list complianceData — object identity churn re-ran this effect and aborted the in-flight POST.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisTabActive, isHealthcareFacility, selectedFacility?.facility_id]);

  const facilityPageAuditLoggedRef = React.useRef<string | null>(null);

  // Log page access to audit log when the Facility Analysis tab is visible
  useEffect(() => {
    if (!analysisTabActive) {
      facilityPageAuditLoggedRef.current = null;
      return;
    }
    const facility =
      selectedFacility ?? getSelectedFacilityFromSession<Facility>();
    const facilityId = facility?.facility_id;
    if (!facilityId) return;
    const key = String(facilityId);
    if (facilityPageAuditLoggedRef.current === key) return;
    facilityPageAuditLoggedRef.current = key;

    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      
      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: '/api/v1/facility-compliance/',
        method: 'GET',
        request_payload: {},
        response_payload: {},
        status_code: 200,
        before_value: null,
        after_value: null,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: 'Facility Analysis',
          action: 'VIEW',
        },
        action_type: 'VIEW',
        action_status: 'SUCCESS',
      });
    };
    
    void logPageAccess();
  }, [analysisTabActive, selectedFacility]);

  const fetchFederalCompliance = async () => {
    if (!isHealthcareFacility) {
      return;
    }
    const facility = selectedFacilityRef.current;
    if (!facility) {
      return;
    }
    if (federalComplianceInFlightRef.current) {
      return;
    }
    const facilityIdAtStart = facility.facility_id;
    const myOpId = federalComplianceOpIdRef.current;
    const abortController = new AbortController();
    // Only the facility-change effect aborts a prior in-flight POST. Do not abort here:
    // a duplicate prefetch used to cancel the client fetch while the server LLM kept running.
    federalComplianceAbortRef.current = abortController;
    const timeoutId = window.setTimeout(() => {
      abortController.abort();
    }, FEDERAL_COMPLIANCE_FETCH_TIMEOUT_MS);

    federalComplianceInFlightRef.current = true;
    setFederalComplianceLoading(true);
    setFederalComplianceError(null);
    try {
      const facilityCountryUpper = String(
        facility.country_code || getSelectedCountryCode() || "USA",
      )
        .trim()
        .toUpperCase();
      const isCanada = isCanadaCountryCode(facilityCountryUpper);
      const countryForApi =
        facilityCountryUpper === "CAN" || facilityCountryUpper === "CA"
          ? "Canada"
          : facilityCountryUpper === "US" || facilityCountryUpper === "USA"
            ? "USA"
            : facility.country_code || getSelectedCountryCode() || "USA";

      const profileFlags = deriveFederalComplianceFacilityFlags(facility);
      // Build the payload using selectedFacility
      const payload: FederalCompliancePayload = {
        country: countryForApi,
        facilityType:
          facility.facility_type || "",
        state: facility.state || " ",
        cityOrCounty: facility.city || " ",
        constructionOrFitOut: true,
        dispensesOrStoresDrugs: profileFlags.dispensesOrStoresDrugs,
        emergencyServices: false,
        handlesControlledSubstances: profileFlags.handlesControlledSubstances,
        instructions: isCanada
          ? "Provide Canadian national and cross-cutting regulatory requirements: Health Canada themes where applicable, PIPEDA and applicable provincial/territorial health privacy law, occupational health and safety (federal and provincial), controlled drugs and substances, radiation/medical devices as applicable. Use the API JSON shape only (no root timeline). Do not include U.S. CMS Medicare/Medicaid enrollment, U.S. Conditions of Participation (CoPs), or U.S. Conditions for Coverage (CfCs)."
          : "Provide federal compliance requirements (CMS, HIPAA, OSHA, and other federal themes as applicable) in the API JSON shape only — no separate implementation timeline section.",
        org_name: facility.facility_name || "test",
        output_format: "JSON",
        ownership: "Private",
        payers: isCanada
          ? ["Provincial health plans", "Private Insurance", "Self-pay"]
          : ["Medicare", "Medicaid", "Private Insurance"],
        question: isCanada
          ? "What are the national and provincial compliance requirements for operating this healthcare facility in Canada?"
          : "What are the federal compliance requirements for this facility?",
        response_language: "English",
        servicesOffered: ["General Medical Services"],
        usesOnsiteLab: profileFlags.usesOnsiteLab,
        usesRadiationOrImaging: profileFlags.usesRadiationOrImaging,
        federal_compliance_only: true,
      };

      const data = await facilityComplianceService.getFederalCompliance(
        payload,
        { signal: abortController.signal },
      );

      if (federalComplianceOpIdRef.current !== myOpId) {
        return;
      }
      if (selectedFacilityRef.current?.facility_id !== facilityIdAtStart) {
        return;
      }
      federalComplianceLoadedForFacilityIdRef.current = facilityIdAtStart;
      federalPrefetchScheduledForFacilityRef.current = facilityIdAtStart;
      setFederalComplianceData(data);
      setFederalComplianceError(null);
    } catch (error) {
      if (federalComplianceOpIdRef.current !== myOpId) {
        return;
      }
      if (selectedFacilityRef.current?.facility_id !== facilityIdAtStart) {
        return;
      }
      const aborted =
        (error instanceof DOMException && error.name === "AbortError") ||
        (error instanceof Error && error.name === "AbortError");
      if (!aborted) {
        console.error("Error fetching federal compliance data:", error);
      }
      if (aborted) {
        if (federalPrefetchScheduledForFacilityRef.current === facilityIdAtStart) {
          federalPrefetchScheduledForFacilityRef.current = null;
        }
        setFederalComplianceError(
          "This request took too long and was stopped. You can try again.",
        );
      } else {
        if (federalPrefetchScheduledForFacilityRef.current === facilityIdAtStart) {
          federalPrefetchScheduledForFacilityRef.current = null;
        }
        const msg =
          error instanceof Error
            ? error.message
            : "Failed to load compliance requirements.";
        setFederalComplianceError(msg);
      }
    } finally {
      window.clearTimeout(timeoutId);
      if (federalComplianceOpIdRef.current === myOpId) {
        if (federalComplianceAbortRef.current === abortController) {
          federalComplianceAbortRef.current = null;
        }
        federalComplianceInFlightRef.current = false;
        setFederalComplianceLoading(false);
      }
    }
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
      // Fallback only if nothing is in flight (prefetch may already be running — avoid duplicate LLM).
      if (
        panel === "facilityCompliance" &&
        isExpanded &&
        isHealthcareFacility &&
        !federalComplianceData &&
        !federalComplianceLoading &&
        !federalComplianceInFlightRef.current &&
        !federalComplianceError
      ) {
        void fetchFederalCompliance();
      }
    };

  const handleCfrPartChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCfrPart(isExpanded ? panel : false);
    };

  const handleSourceDocumentChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSourceDocument(isExpanded ? panel : false);
    };

  const renderComparisonCitation = (citation: any) => {
    if (!citation?.title) return "—";
    if (!citation.url) return citation.title;
    return (
      <a
        href={citation.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#0ea5e9", textDecoration: "none" }}
      >
        {citation.title}
      </a>
    );
  };

  const renderComparisonStatusTable = (
    title: string,
    icon: React.ReactNode,
    rows: any[] | undefined,
  ) => {
    if (!rows?.length) return null;

    return (
      <Box
        sx={{
          backgroundColor: "#f8fdfe",
          padding: "8px",
          borderRadius: "8px",
          border: "1px solid #cff7ff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <Box sx={{ width: "20px", height: "20px", flexShrink: 0 }}>{icon}</Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#065b70",
              fontFamily: "Rethink Sans, sans-serif",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            border: "1px solid #5bbfd6",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
              gap: "12px",
              backgroundColor: "#e8f7fa",
              borderBottom: "1px solid #99d4e1",
              px: "12px",
              py: "10px",
            }}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#065b70" }}>
              Requirement
            </Typography>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#065b70" }}>
              Source
            </Typography>
          </Box>
          {rows.map((item, index) => {
            const citation = typeof item === "object" ? item.citation : null;
            return (
              <Box
                key={`${title}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
                  gap: "12px",
                  px: "12px",
                  py: "10px",
                  borderTop: index > 0 ? "1px solid #e5f5f8" : "none",
                  "& > *": { minWidth: 0 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#065b70",
                    lineHeight: 1.45,
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  {typeof item === "string"
                    ? item
                    : getPendingItemDisplayText(item, complianceData?.facilityContext)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#065b70",
                    lineHeight: 1.45,
                    fontFamily: "Rethink Sans, sans-serif",
                  }}
                >
                  {renderComparisonCitation(citation)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderComparisonDeadlinesTable = (rows: any[] | undefined) => {
    if (!rows?.length) return null;

    return (
      <Box
        sx={{
          backgroundColor: "#f8fdfe",
          padding: "8px",
          borderRadius: "8px",
          border: "1px solid #cff7ff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <Box sx={{ width: "20px", height: "20px", flexShrink: 0 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M14.5 3C15.8807 3 17 4.11929 17 5.5V9.59971C16.6832 9.43777 16.3486 9.30564 16 9.20703V7H4V14.5C4 15.3284 4.67157 16 5.5 16H9.20703C9.30564 16.3486 9.43777 16.6832 9.59971 17H5.5C4.11929 17 3 15.8807 3 14.5V5.5C3 4.11929 4.11929 3 5.5 3H14.5ZM14.5 4H5.5C4.67157 4 4 4.67157 4 5.5V6H16V5.5C16 4.67157 15.3284 4 14.5 4ZM19 14.5C19 16.9853 16.9853 19 14.5 19C12.0147 19 10 16.9853 10 14.5C10 12.0147 12.0147 10 14.5 10C16.9853 10 19 12.0147 19 14.5ZM14.5 15.2071L15.6464 16.3536C15.8417 16.5488 16.1583 16.5488 16.3536 16.3536C16.5488 16.1583 16.5488 15.8417 16.3536 15.6464L15.2071 14.5L16.3536 13.3536C16.5488 13.1583 16.5488 12.8417 16.3536 12.6464C16.1583 12.4512 15.8417 12.4512 15.6464 12.6464L14.5 13.7929L13.3536 12.6464C13.1583 12.4512 12.8417 12.4512 12.6464 12.6464C12.4512 12.8417 12.4512 13.1583 12.6464 13.3536L13.7929 14.5L12.6464 15.6464C12.4512 15.8417 12.4512 16.1583 12.6464 16.3536C12.8417 16.5488 13.1583 16.5488 13.3536 16.3536L14.5 15.2071Z"
                fill="#065B70"
              />
            </svg>
          </Box>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#065b70" }}>
            Deadlines
          </Typography>
        </Box>
        <Box sx={{ border: "1px solid #5bbfd6", borderRadius: "8px", overflow: "hidden", backgroundColor: "#fff" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1.7fr) minmax(8rem, 0.8fr) minmax(7rem, 0.7fr) minmax(0, 1fr)",
              gap: "12px",
              backgroundColor: "#e8f7fa",
              borderBottom: "1px solid #99d4e1",
              px: "12px",
              py: "10px",
            }}
          >
            {["Name", "Rule", "Due", "Frequency", "Source"].map((header) => (
              <Typography key={header} sx={{ fontSize: "13px", fontWeight: 700, color: "#065b70" }}>
                {header}
              </Typography>
            ))}
          </Box>
          {rows.map((item, index) => (
            <Box
              key={`deadline-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1.7fr) minmax(8rem, 0.8fr) minmax(7rem, 0.7fr) minmax(0, 1fr)",
                gap: "12px",
                px: "12px",
                py: "10px",
                borderTop: index > 0 ? "1px solid #e5f5f8" : "none",
                "& > *": { minWidth: 0 },
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#065b70" }}>
                {item.name || "—"}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#065b70", lineHeight: 1.45 }}>
                {item.rule || "—"}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#065b70" }}>
                {item.nextDueDate ? formatDate(item.nextDueDate) : "—"}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#065b70" }}>
                {item.frequency || "—"}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#065b70" }}>
                {renderComparisonCitation(item.citation)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  if (!complianceData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          minHeight: "40vh",
        }}
      >
        {error ? (
          <>
            <Typography
              role="alert"
              sx={{ color: "#b42318", fontSize: 18, fontWeight: 600, textAlign: "center" }}
            >
              {error}
            </Typography>
            {onRetry && (
              <Button variant="contained" onClick={onRetry}>
                Retry
              </Button>
            )}
          </>
        ) : (
          <CircularProgress size={48} sx={{ color: "#0a829d" }} />
        )}
        <Typography sx={{ color: "#737373", fontSize: 18, fontWeight: 500, textAlign: "center" }}>
          {error ? "Facility compliance analysis unavailable" : "Analyzing facility compliance data…"}
        </Typography>
        <Typography sx={{ color: "#737373", fontSize: 16, fontWeight: 400, textAlign: "center" }}>
          This AI-assisted analysis may take a few minutes.
        </Typography>
      </Box>
    );
  }

  const requiredSections = [
    complianceData.sections?.licensingRequirements,
    complianceData.sections?.accreditationRequirements,
    complianceData.sections?.complianceComparison,
  ];
  const analysisReturnedEmpty =
    requiredSections.every(
      (section) =>
        !hasMeaningfulAnalysisValue(section),
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {analysisReturnedEmpty && (
        <Box role="alert" sx={{ p: 2, mb: 1, border: "1px solid #f0b429", borderRadius: 2 }}>
          <Typography sx={{ color: "#7a4d00", fontWeight: 600 }}>
            Analysis returned no compliance requirements.
          </Typography>
          {onRetry && (
            <Button variant="outlined" onClick={onRetry} sx={{ mt: 1 }}>
              Retry
            </Button>
          )}
        </Box>
      )}
      {/* Header with Back Button */}

      {/* Compliance Analysis Header */}
      <ComplianceHeader>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
          >
            <path
              d="M8.5 6.5V15.5H11.5V6.5C11.5 5.94772 11.0523 5.5 10.5 5.5H9.5C8.94772 5.5 8.5 5.94772 8.5 6.5ZM7.5 9.5V6.5C7.5 5.39543 8.39543 4.5 9.5 4.5H10.5C11.6046 4.5 12.5 5.39543 12.5 6.5V7.5H14C15.1046 7.5 16 8.39543 16 9.5V16C16 16.2761 15.7761 16.5 15.5 16.5H4.5C4.22386 16.5 4 16.2761 4 16V11.5C4 10.3954 4.89543 9.5 6 9.5H7.5ZM7.5 15.5V10.5H6C5.44772 10.5 5 10.9477 5 11.5V15.5H7.5ZM12.5 15.5H15V9.5C15 8.94772 14.5523 8.5 14 8.5H12.5V15.5Z"
              fill="#065B70"
            />
          </svg>
          <Typography
            sx={{
              color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
            }}
          >
            Compliance Analysis Complete
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {showWatchGuide && (
          <Button
          variant="outlined"
          disabled={!onWatchGuideClick || watchGuideLoading}
          onClick={onWatchGuideClick}
          sx={{
            display: "flex",
            flexShrink: 0,
            minHeight: "40px",
            padding: "8px 16px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            borderRadius: "8px",
            border: "1px solid #5BBFD6",
            background: "#FFFFFF",
            boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F8FDFF",
              borderColor: "#3CA9C2",
            },
          }}
        >
          {watchGuideIconSrc ? (
            <Box
              component="img"
              src={watchGuideIconSrc}
              alt="Watch guide"
              sx={{ width: 16, height: 16, display: "block" }}
            />
          ) : null}
          <Typography
            sx={{
              color: "#086E86",
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              lineHeight: "normal",
            }}
          >
            {watchGuideLoading ? "Loading Guide..." : "Watch Guide"}
          </Typography>
          </Button>
        )}
      </ComplianceHeader>
      {/* <Typography
        sx={{
          color: "#525252",
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: "18px",
          ml: "28px",
        }}
      >
        AI-assisted analysis. Human review and verification required.
      </Typography> */}
      {/* Accordion Sections */}
      <Box sx={{ width: "100%", mt: "12px" }}>
        {/* Certificate of Need Summary - Only show if there is an active CON with a non-zero approved capital cost */}
        {(() => {
          const con = complianceData?.sections?.certificateOfNeedSummary;
          const amount = con?.approvedCapitalCost?.amount ?? null;
          return amount != null && Number(amount) !== 0;
        })() && (
        <StyledAccordion
          expanded={expanded === "certificate"}
          onChange={handleAccordionChange("certificate")}
        >
          <AccordionSummary
            expandIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            sx={{
              borderRadius: "4px",
              border: "1px solid #9EE5F4",
              backgroundColor: "#FFF",
              boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
              "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                transform: "rotate(180deg)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                minWidth: 0,
                pr: 0.5,
              }}
            >
              <MenuIcon />
              <AccordionTitle
                className="accordion-title"
                sx={{ flex: 1, minWidth: 0 }}
              >
                Provide a health department summary, Certificate of Need, or
                other.
              </AccordionTitle>
              <Box
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <GlobalAdminLlmPromptButton
                  dialogTitle="Certificate of Need — LLM prompt"
                  promptText={complianceData.llmPromptsBySection?.con}
                  wallTimeSec={complianceData.llmInvokeWallSecondsBySection?.con}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: "#ffffff",
              // border: "2px solid #35d7e4",
              // borderRadius: "4px",
              padding: "12px",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {(() => {
                const con = complianceData?.sections?.certificateOfNeedSummary;
                const showProjectDescription =
                  con?.projectDescription != null &&
                  String(con.projectDescription).trim() !== "";
                const showConditionsOfApproval =
                  Array.isArray(con?.conditionsOfApproval) &&
                  con.conditionsOfApproval.length > 0;
                const acc = con?.approvedCapitalCost;
                const amount = acc?.amount ?? null;
                const hasAmount =
                  amount != null && Number(amount) !== 0;
                const showApprovedCapitalCost = hasAmount;
                const t = con?.timeline;
                const showTimeline =
                  t &&
                  [t.applicationDate, t.reviewDate, t.issuanceDate, t.expirationDate].some(
                    (d) => d != null && String(d).trim() !== ""
                  );
                return (
                  <>
              {/* Project Description */}
              {showProjectDescription && (
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Project Description
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: "#f8fdfe",
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid #5bbfd6",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#065b70",
                      lineHeight: 1.4,
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    {complianceData?.sections?.certificateOfNeedSummary
                      ?.projectDescription}
                  </Typography>
                </Box>
              </Box>
              )}

              {/* Conditions of Approval */}
              {showConditionsOfApproval && (
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Conditions of Approval
                  </Typography>
                </Box>
                {(complianceData?.sections?.certificateOfNeedSummary
                  ?.conditionsOfApproval || []
                ).map((condition, index, allConditions) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: "#f8fdfe",
                      padding: "8px",
                      borderRadius: "8px",
                      border: "1px solid #5bbfd6",
                      marginBottom:
                        index < allConditions.length - 1 ? "8px" : "0",
                    }}
                  >
                      <Box
                        sx={{
                          borderBottom: "1px solid #3095ab",
                          paddingBottom: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          Condition {index + 1}
                        </Typography>
                      </Box>
                      <Box sx={{ paddingLeft: "8px" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#065b70",
                            lineHeight: 1.4,
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {condition.text}
                        </Typography>
                      </Box>
                    </Box>
                  )
                )}
              </Box>
              )}

              {/* Approved Capital Cost */}
              {showApprovedCapitalCost && (
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Approved Capital Cost
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: "#f8fdfe",
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid #5bbfd6",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderBottom: "1px solid #3095ab",
                      paddingBottom: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: "90px",
                        paddingX: "24px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        Capital Expenditure
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#fcfeff",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        border: "1px solid #99d4e1",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        {formatCurrencyAmount(
                          complianceData.sections.certificateOfNeedSummary
                            ?.approvedCapitalCost?.amount ?? 0
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ paddingLeft: "8px" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#065b70",
                        lineHeight: 1.4,
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      {complianceData.sections.certificateOfNeedSummary
                        ?.approvedCapitalCost?.notes ||
                        "No additional notes available"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              )}

              {/* Timeline */}
              {showTimeline && (
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Timeline
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: "#f8fdfe",
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid #5bbfd6",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {[
                    {
                      label: "Application :",
                      value:
                          complianceData.sections.certificateOfNeedSummary
                            ?.timeline?.applicationDate,
                    },
                    {
                      label: "Review :",
                      value:
                          complianceData.sections.certificateOfNeedSummary
                            ?.timeline?.reviewDate,
                    },
                    {
                      label: "Issuance :",
                      value:
                          complianceData.sections.certificateOfNeedSummary
                            ?.timeline?.issuanceDate,
                    },
                    {
                      label: "Expiration :",
                      value:
                          complianceData.sections.certificateOfNeedSummary
                            ?.timeline?.expirationDate,
                    },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: "128px",
                          paddingX: "24px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: "#fcfeff",
                          padding: "8px 12px",
                          borderRadius: "4px",
                          border: "1px solid #99d4e1",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {formatDate(item.value) || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
              )}

                  </>
                );
              })()}

              {/* Key Findings */}
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Key Findings
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {[
                    {
                      title: "Need",
                      content:
                        complianceData?.sections?.certificateOfNeedSummary
                          ?.findings?.need?.text ?? "No finding available",
                    },
                    {
                      title: "Financial Feasibility:",
                      content:
                        complianceData?.sections?.certificateOfNeedSummary
                          ?.findings?.financialFeasibility?.text ??
                        "No finding available",
                    },
                    {
                      title: "Structure/Process of Care:",
                      content:
                        complianceData?.sections?.certificateOfNeedSummary
                          ?.findings?.structureProcessOfCare?.text ??
                        "No finding available",
                    },
                    {
                      title: "Cost Containment:",
                      content:
                        complianceData?.sections?.certificateOfNeedSummary
                          ?.findings?.costContainment?.text ??
                        "No finding available",
                    },
                  ].map((finding, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: "#f8fdfe",
                        padding: "8px",
                        borderRadius: "8px",
                        border: "1px solid #5bbfd6",
                      }}
                    >
                      <Box
                        sx={{
                          borderBottom: "1px solid #3095ab",
                          paddingBottom: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {finding.title}
                        </Typography>
                      </Box>
                      <Box sx={{ paddingLeft: "8px" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#065b70",
                            lineHeight: 1.4,
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {finding.content}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </StyledAccordion>
        )}

        {/* Licensing Requirements */}
        <StyledAccordion
          expanded={expanded === "licensing"}
          onChange={handleAccordionChange("licensing")}
        >
          <AccordionSummary
            expandIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            sx={{
              borderRadius: "4px",
              border: "1px solid #9EE5F4",
              backgroundColor: "#FFF",
              boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
              "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                transform: "rotate(180deg)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                minWidth: 0,
                pr: 0.5,
              }}
            >
              <LicencingIcon />
              <AccordionTitle
                className="accordion-title"
                sx={{ flex: 1, minWidth: 0 }}
              >
                What are the licensing requirements
              </AccordionTitle>
              <Box
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <GlobalAdminLlmPromptButton
                  dialogTitle="Licensing — LLM prompt"
                  promptText={complianceData.llmPromptsBySection?.licensing}
                  wallTimeSec={
                    complianceData.llmInvokeWallSecondsBySection?.licensing
                  }
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: "#ffffff",
              // border: "2px solid #35d7e4",
              // borderRadius: "4px",
              padding: "12px",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {/* National Level */}
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    National Level
                  </Typography>
                </Box>
                <LicensingRequirementsTable
                  licenses={
                    complianceData.sections.licensingRequirements.country
                  }
                />
              </Box>

              {/* State/Province Level */}
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    State/Province Level
                  </Typography>
                </Box>
                <LicensingRequirementsTable
                  licenses={complianceData.sections.licensingRequirements.state}
                />
              </Box>

              {/* County Level */}
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    County Level
                  </Typography>
                </Box>
                <LicensingRequirementsTable
                  licenses={
                    complianceData.sections.licensingRequirements.county
                  }
                />
              </Box>

              {/* City Level */}
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    City Level
                  </Typography>
                </Box>
                <LicensingRequirementsTable
                  licenses={complianceData.sections.licensingRequirements.city}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </StyledAccordion>

        {/* Accreditation Requirements */}
        {isHealthcareFacility && (
        <StyledAccordion
          expanded={expanded === "accreditation"}
          onChange={handleAccordionChange("accreditation")}
        >
          <AccordionSummary
            expandIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            sx={{
              borderRadius: "4px",
              border: "1px solid #9EE5F4",
              backgroundColor: "#FFF",
              boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
              "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                transform: "rotate(180deg)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                minWidth: 0,
                pr: 0.5,
              }}
            >
              <AccreditationIcon />
              <AccordionTitle
                className="accordion-title"
                sx={{ flex: 1, minWidth: 0 }}
              >
                What are the accreditation requirements?
              </AccordionTitle>
              <Box
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <GlobalAdminLlmPromptButton
                  dialogTitle="Accreditation — LLM prompt"
                  promptText={
                    complianceData.llmPromptsBySection?.accreditation
                  }
                  wallTimeSec={
                    complianceData.llmInvokeWallSecondsBySection?.accreditation
                  }
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: "#ffffff",
              // border: "2px solid #35d7e4",
              // borderRadius: "4px",
              padding: "12px",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {/* Conditions of Participation */}
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Conditions of Participation
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {complianceData.sections.accreditationRequirements.conditionsOfParticipation.map(
                    (condition, index) => (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "16px 8px",
                          borderRadius: "8px",
                          border: "1px solid #5bbfd6",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            lineHeight: 1.4,
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {condition}
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              </Box>

              {/* Accrediting Organizations */}
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Accrediting Organizations
                  </Typography>
                </Box>
                {complianceData.sections.accreditationRequirements.accreditingOrganizations.map(
                  (org, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: "#f8fdfe",
                        padding: "8px",
                        borderRadius: "8px",
                        border: "1px solid #5bbfd6",
                        marginBottom:
                          index <
                          complianceData.sections.accreditationRequirements
                            .accreditingOrganizations.length -
                            1
                            ? "8px"
                            : "0",
                      }}
                    >
                      <Box
                        sx={{
                          borderBottom: "1px solid #3095ab",
                          paddingBottom: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {org.citation?.url ? (
                            <a
                              href={org.citation.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#2563eb",
                                textDecoration: "none",
                              }}
                            >
                              {org.name}
                            </a>
                          ) : (
                            org.name
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ paddingLeft: "8px" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#065b70",
                            lineHeight: 1.4,
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {org.deemedStatusRequirements}
                        </Typography>
                      </Box>
                      {org.citation?.url && org.citation?.title && (
                        <Box sx={{ paddingLeft: "8px", marginTop: "8px" }}>
                          <Box
                            sx={{
                              backgroundColor: "white",
                              padding: "4px 16px",
                              borderRadius: "16px",
                              border: "1px solid #ccf2f9",
                              display: "inline-block",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#065b70",
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              <a
                                href={org.citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#6b7280",
                                  textDecoration: "none",
                                }}
                              >
                                📎 {org.citation.title}
                              </a>
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )
                )}
              </Box>

              {/* Program-Specific Requirements */}
              <Box
                sx={{
                  backgroundColor: "#f8fdfe",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #cff7ff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <DataHistogramIcon size={16} color="#065b70" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    Program-Specific Requirements
                  </Typography>
                </Box>
                {complianceData.sections.accreditationRequirements.programSpecific.map(
                  (program, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: "#f8fdfe",
                        padding: "8px",
                        borderRadius: "8px",
                        border: "1px solid #5bbfd6",
                        marginBottom:
                          index <
                          complianceData.sections.accreditationRequirements
                            .programSpecific.length -
                            1
                            ? "8px"
                            : "0",
                      }}
                    >
                      <Box
                        sx={{
                          borderBottom: "1px solid #3095ab",
                          paddingBottom: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {program.citation?.url ? (
                            <a
                              href={program.citation.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#2563eb",
                                textDecoration: "none",
                              }}
                            >
                              {program.program}
                            </a>
                          ) : (
                            program.program
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ paddingLeft: "8px", marginBottom: "8px" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#065b70",
                            lineHeight: 1.4,
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {program.requirements}
                        </Typography>
                      </Box>
                      <Box sx={{ paddingLeft: "8px" }}>
                        <Box
                          sx={{
                            backgroundColor: "white",
                            padding: "4px 16px",
                            borderRadius: "16px",
                            border: "1px solid #ccf2f9",
                            display: "inline-block",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            {program.citation?.url ? (
                              <a
                                href={program.citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#6b7280",
                                  textDecoration: "none",
                                }}
                              >
                                📎 {program.citation.title}
                              </a>
                            ) : (
                              <>Source: {program.citation?.title}</>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </StyledAccordion>
        )}

        {/* Facility Compliance Requirements */}
        {isHealthcareFacility && (
        <StyledAccordion
          expanded={expanded === "facilityCompliance"}
          onChange={handleAccordionChange("facilityCompliance")}
          aria-busy={federalComplianceLoading}
        >
          <AccordionSummary
            expandIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            sx={{
              borderRadius: "4px",
              border: "1px solid #9EE5F4",
              backgroundColor: "#FFF",
              boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
              "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                transform: "rotate(180deg)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
                pr: 0.5,
              }}
            >
              <ComplianceIcon />
              <AccordionTitle
                className="accordion-title"
                sx={{ flex: 1, minWidth: 0 }}
              >
                What are facility compliance requirements?
              </AccordionTitle>
              {federalComplianceLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexShrink: 0,
                  }}
                  aria-live="polite"
                >
                  <CircularProgress size={20} thickness={5} sx={{ color: "#0a829d" }} />
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: "#0a829d", whiteSpace: "nowrap" }}
                  >
                    Loading…
                  </Typography>
                </Box>
              ) : null}
              <Box
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <GlobalAdminLlmPromptButton
                  dialogTitle="Federal compliance — LLM prompt"
                  promptText={
                    typeof federalComplianceData?.llmPrompt === "string"
                      ? federalComplianceData.llmPrompt
                      : undefined
                  }
                  wallTimeSec={
                    typeof federalComplianceData?.llmInvokeWallSec ===
                    "number"
                      ? federalComplianceData.llmInvokeWallSec
                      : undefined
                  }
                />
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              backgroundColor: "#ffffff",
              padding: "12px",
              position: "relative",
            }}
          >
            {federalComplianceLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "40px",
                }}
              >
                <CircularProgress size={40} sx={{ color: "#0a829d" }} />
              </Box>
            ) : federalComplianceData ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                {/* Context Section
                {federalComplianceData.context && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#065b70",
                        fontFamily: "Rethink Sans, sans-serif",
                        marginBottom: "12px",
                      }}
                    >
                      Context
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "#f8fdfe",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #cff7ff",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "13px", marginBottom: "8px" }}
                      >
                        <strong>Facility Type:</strong>{" "}
                        {federalComplianceData.context.facilityType}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "13px", marginBottom: "8px" }}
                      >
                        <strong>Location:</strong>{" "}
                        {federalComplianceData.context.cityOrCounty},{" "}
                        {federalComplianceData.context.state}
                      </Typography>
                      {federalComplianceData.context.assumptions && (
                        <Box sx={{ marginTop: "12px" }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              marginBottom: "8px",
                            }}
                          >
                            Assumptions:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: "20px" }}>
                            {federalComplianceData.context.assumptions.map(
                              (assumption: string, index: number) => (
                                <li
                                  key={index}
                                  style={{
                                    fontSize: "13px",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {assumption}
                                </li>
                              )
                            )}
                          </ul>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )} */}

                {/* Federal Compliance Requirements Section */}
                {federalComplianceData.federalCompliance && (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      <Box sx={{ width: "20px", height: "20px" }}>
                        <DataHistogramIcon size={16} color="#065b70" />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        {isCanadaCountryCode(
                          federalComplianceData.context?.country,
                        ) ||
                        isCanadaCountryCode(effectiveFacility?.country_code) ||
                        isCanadaCountryCode(getSelectedCountryCode())
                          ? "National compliance requirements"
                          : "Federal Compliance Requirements"}
                      </Typography>
                    </Box>

                    {/* CMS */}
                    {federalComplianceData.federalCompliance.CMS && (
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                            marginBottom: "8px",
                          }}
                        >
                          CMS
                        </Typography>

                        {/* Enrollment */}
                        {federalComplianceData.federalCompliance.CMS
                          .enrollment && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Enrollment
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              {federalComplianceData.federalCompliance.CMS.enrollment.map(
                                (item: any, index: number) => (
                                  <Typography
                                    key={index}
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      lineHeight: 1.6,
                                      fontFamily: "Rethink Sans, sans-serif",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    • {item.text}
                                    {item.citation && (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          fontSize: "12px",
                                          color: "#737373",
                                          marginLeft: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        <a
                                          href={item.citation.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#0a829d",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {item.citation.title}
                                        </a>
                                      </Box>
                                    )}
                                  </Typography>
                                )
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* CoPs or CfCs */}
                        {federalComplianceData.federalCompliance.CMS
                          .CoPsOrCfCs && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                CoPs Or CfCs
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              {federalComplianceData.federalCompliance.CMS.CoPsOrCfCs.map(
                                (item: any, index: number) => (
                                  <Typography
                                    key={index}
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      lineHeight: 1.6,
                                      fontFamily: "Rethink Sans, sans-serif",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    • {item.text}
                                    {item.citation && (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          fontSize: "12px",
                                          color: "#737373",
                                          marginLeft: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        <a
                                          href={item.citation.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#0a829d",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {item.citation.title}
                                        </a>
                                      </Box>
                                    )}
                                  </Typography>
                                )
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* HIPAA */}
                    {federalComplianceData.federalCompliance.HIPAA && (
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                            marginBottom: "8px",
                          }}
                        >
                          HIPAA
                        </Typography>

                        {/* Required Policies */}
                        {federalComplianceData.federalCompliance.HIPAA
                          .requiredPolicies && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Required Policies
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              {federalComplianceData.federalCompliance.HIPAA.requiredPolicies.map(
                                (item: any, index: number) => (
                                  <Typography
                                    key={index}
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      lineHeight: 1.6,
                                      fontFamily: "Rethink Sans, sans-serif",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    • {item.text}
                                    {item.citation && (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          fontSize: "12px",
                                          color: "#737373",
                                          marginLeft: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        <a
                                          href={item.citation.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#0a829d",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {item.citation.title}
                                        </a>
                                      </Box>
                                    )}
                                  </Typography>
                                )
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* Risk Analysis Tasks */}
                        {federalComplianceData.federalCompliance.HIPAA
                          .riskAnalysisTasks && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Risk Analysis Tasks
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              {federalComplianceData.federalCompliance.HIPAA.riskAnalysisTasks.map(
                                (item: any, index: number) => (
                                  <Typography
                                    key={index}
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      lineHeight: 1.6,
                                      fontFamily: "Rethink Sans, sans-serif",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    • {item.text}
                                    {item.citation && (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          fontSize: "12px",
                                          color: "#737373",
                                          marginLeft: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        <a
                                          href={item.citation.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#0a829d",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {item.citation.title}
                                        </a>
                                      </Box>
                                    )}
                                  </Typography>
                                )
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* OSHA */}
                    {federalComplianceData.federalCompliance.OSHA && (
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                            marginBottom: "8px",
                          }}
                        >
                          OSHA
                        </Typography>

                        {/* Programs */}
                        {federalComplianceData.federalCompliance.OSHA
                          .programs && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Programs
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              {federalComplianceData.federalCompliance.OSHA.programs.map(
                                (item: any, index: number) => (
                                  <Typography
                                    key={index}
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      lineHeight: 1.6,
                                      fontFamily: "Rethink Sans, sans-serif",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    • {item.text}
                                    {item.citation && (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          fontSize: "12px",
                                          color: "#737373",
                                          marginLeft: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        <a
                                          href={item.citation.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#0a829d",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {item.citation.title}
                                        </a>
                                      </Box>
                                    )}
                                  </Typography>
                                )
                              )}
                            </Box>
                          </Box>
                        )}
                        {/* Training */}
                        {federalComplianceData.federalCompliance.OSHA
                          .training && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Training
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              {federalComplianceData.federalCompliance.OSHA.training.map(
                                (item: any, index: number) => (
                                  <Typography
                                    key={index}
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      lineHeight: 1.6,
                                      fontFamily: "Rethink Sans, sans-serif",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    • {item.text}
                                    {item.citation && (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          fontSize: "12px",
                                          color: "#737373",
                                          marginLeft: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        <a
                                          href={item.citation.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            color: "#0a829d",
                                            textDecoration: "underline",
                                          }}
                                        >
                                          {item.citation.title}
                                        </a>
                                      </Box>
                                    )}
                                  </Typography>
                                )
                              )}
                            </Box>
                          </Box>
                        )}
                        {/* Penalties */}
                        {/* {federalComplianceData.federalCompliance.OSHA
                          .penalties && (
                          <Box
                            sx={{
                              backgroundColor: "#fff3cd",
                              padding: "8px",
                              borderRadius: "4px",
                              border: "1px solid #ffc107",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#856404",
                                fontFamily: "Rethink Sans, sans-serif",
                                marginBottom: "4px",
                              }}
                            >
                              Penalties:
                            </Typography>
                            <Typography
                              sx={{ fontSize: "12px", color: "#856404" }}
                            >
                              Baseline:{" "}
                              {
                                federalComplianceData.federalCompliance.OSHA
                                  .penalties.baseline
                              }
                              <br />
                              Adjusted 2025:{" "}
                              {
                                federalComplianceData.federalCompliance.OSHA
                                  .penalties.adjusted2025
                              }
                              <br />
                              Effective Date:{" "}
                              {
                                federalComplianceData.federalCompliance.OSHA
                                  .penalties.effectiveDate
                              }
                            </Typography>
                          </Box>
                        )} */}
                      </Box>
                    )}

                    {renderFederalComplianceDynamicSections(
                      federalComplianceData.federalCompliance as Record<
                        string,
                        unknown
                      >,
                    )}

                    {/* Implementation Timeline Section */}
                    {federalComplianceData.timeline && (() => {
                      const conAmount = complianceData?.sections?.certificateOfNeedSummary?.approvedCapitalCost?.amount ?? null;
                      const hasActiveCON = conAmount != null && Number(conAmount) !== 0;
                      return hasActiveCON;
                    })() && (
                      <Box sx={{ marginTop: "24px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "16px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Implementation Timeline
                          </Typography>
                        </Box>

                        {federalComplianceData.timeline.map(
                          (phase: any, index: number) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                  marginBottom: "8px",
                                }}
                              >
                                {phase.phase}
                              </Typography>
                              <Box
                                sx={{
                                  backgroundColor: "#f8fdfe",
                                  padding: "8px",
                                  borderRadius: "8px",
                                  border: "1px solid #5bbfd6",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    borderBottom: "1px solid #3095ab",
                                    paddingBottom: "8px",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: "90px",
                                      paddingX: "12px",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "#065b70",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      Duration
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      backgroundColor: "#fcfeff",
                                      padding: "8px 12px",
                                      borderRadius: "4px",
                                      border: "1px solid #99d4e1",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "#065b70",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      {phase.durationWeeks} weeks
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ paddingLeft: "8px" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                      marginBottom: "6px",
                                    }}
                                  >
                                    Milestones:
                                  </Typography>
                                  {phase.milestones.map(
                                    (milestone: string, mIndex: number) => (
                                      <Typography
                                        key={mIndex}
                                        sx={{
                                          fontSize: "14px",
                                          color: "#065b70",
                                          lineHeight: 1.6,
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                          marginBottom: "6px",
                                          paddingLeft: "8px",
                                        }}
                                      >
                                        • {milestone}
                                      </Typography>
                                    )
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ) : federalComplianceError ? (
              <Box
                sx={{
                  padding: "24px",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography sx={{ color: "#b45309", fontSize: "14px" }}>
                  {federalComplianceError}
                </Typography>
                <Button
                  variant="contained"
                  aria-label="Retry"
                  onClick={() => {
                    void fetchFederalCompliance();
                  }}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#0a829d",
                    "&:hover": { backgroundColor: "#086b82" },
                  }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <Box sx={{ padding: "20px", textAlign: "center" }}>
                <Typography sx={{ color: "#737373", fontSize: "14px" }}>
                  Click to expand and load federal compliance requirements
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </StyledAccordion>
        )}

        {/* Requirements Completed and Deadlines */}

        {/* Requirements Completed and Deadlines */}
        <StyledAccordion
          expanded={expanded === "complianceComparison"}
          onChange={handleAccordionChange("complianceComparison")}
        >
          <AccordionSummary
            expandIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            sx={{
              borderRadius: "4px",
              border: "1px solid #9EE5F4",
              backgroundColor: "#FFF",
              boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
              "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                transform: "rotate(180deg)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                minWidth: 0,
                pr: 0.5,
              }}
            >
              <RequirementsIcon />
              <AccordionTitle
                className="accordion-title"
                sx={{ flex: 1, minWidth: 0 }}
              >
                What are the requirements completed, and deadlines?
              </AccordionTitle>
              <Box
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <GlobalAdminLlmPromptButton
                  dialogTitle="Compliance comparison — LLM prompt"
                  promptText={complianceData.llmPromptsBySection?.comparison}
                  wallTimeSec={
                    complianceData.llmInvokeWallSecondsBySection?.comparison
                  }
                />
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              backgroundColor: "#ffffff",
              // border: "2px solid #35d7e4",
              // borderRadius: "4px",
              padding: "12px",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {/* Facility revalidation dates from backend (avoids "last revalidation date not on record") */}
              {(complianceData?.facilityContext?.last_medicare_revalidation_date ||
                complianceData?.facilityContext?.last_medicaid_revalidation_date) && (
                <Box
                  sx={{
                    backgroundColor: "#e8f7fa",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #9EE5F4",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#065b70",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    {complianceData.facilityContext?.last_medicare_revalidation_date && (
                      <>Last Medicare revalidation: {complianceData.facilityContext.last_medicare_revalidation_date}</>
                    )}
                    {complianceData.facilityContext?.last_medicare_revalidation_date &&
                      complianceData.facilityContext?.last_medicaid_revalidation_date && " · "}
                    {complianceData.facilityContext?.last_medicaid_revalidation_date && (
                      <>Last Medicaid revalidation: {complianceData.facilityContext.last_medicaid_revalidation_date}</>
                    )}
                  </Typography>
                </Box>
              )}
              {renderComparisonStatusTable(
                "Completed",
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M6 3C4.34315 3 3 4.34315 3 6V14C3 15.6569 4.34315 17 6 17H14C15.6569 17 17 15.6569 17 14V6C17 4.34315 15.6569 3 14 3H6ZM4 6C4 4.89543 4.89543 4 6 4H14C15.1046 4 16 4.89543 16 6V14C16 15.1046 15.1046 16 14 16H6C4.89543 16 4 15.1046 4 14V6ZM13.3536 8.35355C13.5488 8.15829 13.5488 7.84171 13.3536 7.64645C13.1583 7.45118 12.8417 7.45118 12.6464 7.64645L9 11.2929L7.35355 9.64645C7.15829 9.45118 6.84171 9.45118 6.64645 9.64645C6.45118 9.84171 6.45118 10.1583 6.64645 10.3536L8.64645 12.3536C8.84171 12.5488 9.15829 12.5488 9.35355 12.3536L13.3536 8.35355Z"
                    fill="#065B70"
                  />
                </svg>,
                complianceData.sections.complianceComparison?.completed,
              )}

              {renderComparisonStatusTable(
                "Pending",
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M13.69 14.6C13.8891 15.0402 13.9947 15.5169 14 16H6C6.00608 15.5159 6.11516 15.0387 6.32 14.6C6.52759 14.1497 6.83544 13.753 7.22 13.44L9.04 11.92C9.32073 11.6855 9.54611 11.3918 9.7 11.06C9.85933 10.7294 9.9414 10.367 9.94 10H10.06C10.0741 10.3709 10.1733 10.7336 10.35 11.06C10.5014 11.3878 10.7234 11.678 11 11.91L12.78 13.44C13.1652 13.7548 13.476 14.1509 13.69 14.6ZM5.19531 17.8438C5.42448 17.9479 5.66667 18 5.92188 18H14.0781C14.3385 17.9948 14.5833 17.9427 14.8125 17.8438C15.0417 17.7448 15.2448 17.6042 15.4219 17.4219C15.599 17.2396 15.7396 17.0339 15.8438 16.8047C15.9479 16.5755 16 16.3333 16 16.0781C16 15.6667 15.9557 15.2682 15.8672 14.8828C15.7786 14.4974 15.6536 14.125 15.4922 13.7656C15.3307 13.4062 15.1302 13.0703 14.8906 12.7578C14.651 12.4453 14.375 12.1589 14.0625 11.8984L12.2422 10.3828C12.1849 10.3359 12.1406 10.2786 12.1094 10.2109C12.0781 10.1432 12.0625 10.0729 12.0625 10C12.0625 9.84896 12.1224 9.72135 12.2422 9.61719L14.0625 8.09375C14.6979 7.5625 15.1797 6.9401 15.5078 6.22656C15.8359 5.51302 16 4.74479 16 3.92188C15.9948 3.66146 15.9427 3.41667 15.8438 3.1875C15.7448 2.95833 15.6042 2.75521 15.4219 2.57812C15.2396 2.40104 15.0339 2.26042 14.8047 2.15625C14.5755 2.05208 14.3333 2 14.0781 2H5.92188C5.66146 2.00521 5.41667 2.05729 5.1875 2.15625C4.95833 2.25521 4.75521 2.39583 4.57812 2.57812C4.40104 2.76042 4.26042 2.96615 4.15625 3.19531C4.05208 3.42448 4 3.66667 4 3.92188C4.00521 4.74479 4.17188 5.51302 4.5 6.22656C4.82812 6.9401 5.30729 7.5625 5.9375 8.09375L7.75781 9.61719C7.8151 9.66406 7.85938 9.72135 7.89062 9.78906C7.92188 9.85677 7.9375 9.92708 7.9375 10C7.9375 10.151 7.8776 10.2786 7.75781 10.3828L5.9375 11.8984C5.625 12.1641 5.34896 12.4531 5.10938 12.7656C4.86979 13.0781 4.66667 13.4141 4.5 13.7734C4.33333 14.1328 4.20833 14.5052 4.125 14.8906C4.04167 15.276 4 15.6719 4 16.0781C4.00521 16.3385 4.05729 16.5833 4.15625 16.8125C4.25521 17.0417 4.39583 17.2448 4.57812 17.4219C4.76042 17.599 4.96615 17.7396 5.19531 17.8438ZM14.4062 16.9297C14.2917 16.9766 14.1693 17 14.0391 17H5.96094C5.82031 16.9948 5.69531 16.9688 5.58594 16.9219C5.47656 16.875 5.375 16.8073 5.28125 16.7188C5.1875 16.6302 5.11719 16.5286 5.07031 16.4141C5.02344 16.2995 5 16.1745 5 16.0391C5.00521 15.3828 5.14323 14.763 5.41406 14.1797C5.6849 13.5964 6.07292 13.0938 6.57812 12.6719L8.39844 11.1484C8.57031 11.0026 8.70312 10.8307 8.79688 10.6328C8.89062 10.4349 8.9375 10.224 8.9375 10C8.9375 9.77604 8.89062 9.5625 8.79688 9.35938C8.70312 9.15625 8.57031 8.98438 8.39844 8.84375L6.57812 7.32812C6.31771 7.11458 6.09375 6.88281 5.90625 6.63281C5.71875 6.38281 5.55469 6.11198 5.41406 5.82031C5.27344 5.52865 5.16927 5.22656 5.10156 4.91406C5.03385 4.60156 5 4.28385 5 3.96094C5.00521 3.82031 5.03125 3.69531 5.07812 3.58594C5.125 3.47656 5.19271 3.375 5.28125 3.28125C5.36979 3.1875 5.47135 3.11719 5.58594 3.07031C5.70052 3.02344 5.82552 3 5.96094 3H14.0391C14.1797 3.01042 14.3047 3.03646 14.4141 3.07812C14.5234 3.11979 14.625 3.1875 14.7188 3.28125C14.8125 3.375 14.8828 3.47917 14.9297 3.59375C14.9766 3.70833 15 3.83073 15 3.96094C14.9948 4.6276 14.8568 5.25 14.5859 5.82812C14.3151 6.40625 13.9271 6.90625 13.4219 7.32812L11.6016 8.84375C11.4297 8.98438 11.2969 9.15625 11.2031 9.35938C11.1094 9.5625 11.0625 9.77604 11.0625 10C11.0625 10.224 11.1094 10.4349 11.2031 10.6328C11.2969 10.8307 11.4297 11.0026 11.6016 11.1484L13.4219 12.6719C13.9323 13.099 14.3229 13.6016 14.5938 14.1797C14.8646 14.7578 15 15.3776 15 16.0391C14.9896 16.1797 14.9635 16.3047 14.9219 16.4141C14.8802 16.5234 14.8125 16.625 14.7188 16.7188C14.625 16.8125 14.5208 16.8828 14.4062 16.9297Z"
                    fill="#065B70"
                  />
                </svg>,
                complianceData.sections.complianceComparison?.pending,
              )}

              {renderComparisonStatusTable(
                "Updates",
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5 6.5C5 6.22386 5.22386 6 5.5 6H12.5C12.7761 6 13 6.22386 13 6.5C13 6.77614 12.7761 7 12.5 7H5.5C5.22386 7 5 6.77614 5 6.5ZM10.5 9C10.2239 9 10 9.22386 10 9.5C10 9.77614 10.2239 10 10.5 10H12.5C12.7761 10 13 9.77614 13 9.5C13 9.22386 12.7761 9 12.5 9H10.5ZM10 12.5C10 12.2239 10.2239 12 10.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H10.5C10.2239 13 10 12.7761 10 12.5ZM5.5 9C5.22386 9 5 9.22386 5 9.5V12.5C5 12.7761 5.22386 13 5.5 13H8.5C8.77614 13 9 12.7761 9 12.5V9.5C9 9.22386 8.77614 9 8.5 9H5.5ZM6 12V10H8V12H6ZM2 5C2 3.89543 2.89543 3 4 3H14C15.1046 3 16 3.89543 16 5V6C17.1046 6 18 6.89543 18 8V13.5C18 14.8807 16.8807 16 15.5 16H4.5C3.11929 16 2 14.8807 2 13.5V5ZM15 5C15 4.44772 14.5523 4 14 4L4 4C3.44771 4 3 4.44772 3 5L3 13.5C3 14.3284 3.67157 15 4.5 15H15.5C16.3284 15 17 14.3284 17 13.5V8C17 7.44772 16.5523 7 16 7V13.5C16 13.7761 15.7761 14 15.5 14C15.2239 14 15 13.7761 15 13.5V5Z"
                    fill="#065B70"
                  />
                </svg>,
                complianceData.sections.complianceComparison?.updates,
              )}

              {renderComparisonDeadlinesTable(
                complianceData.sections.complianceComparison?.deadlines,
              )}

              {/* Fallback message when no compliance comparison data */}
              {!(
                (complianceData?.sections?.complianceComparison?.completed?.length ?? 0) > 0 ||
                (complianceData?.sections?.complianceComparison?.pending?.length ?? 0) > 0 ||
                (complianceData?.sections?.complianceComparison?.updates?.length ?? 0) > 0 ||
                (complianceData?.sections?.complianceComparison?.deadlines?.length ?? 0) > 0
              ) && (
                <Box
                  sx={{
                    backgroundColor: "#f8fdfe",
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid #cff7ff",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#6b7280",
                      fontStyle: "italic",
                      fontFamily: "Rethink Sans, sans-serif",
                    }}
                  >
                    No compliance comparison data available. This information will be populated when you provide facility-specific compliance documentation or when the system completes its regulatory analysis.
                  </Typography>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </StyledAccordion>

        {/* CFR Regulation Sections (from aggregator) */}
        {complianceData.sections.cfrRegulationSections &&
          complianceData.sections.cfrRegulationSections.parts.length > 0 && (
          <StyledAccordion
            expanded={expanded === "cfrRegulationSections"}
            onChange={handleAccordionChange("cfrRegulationSections")}
          >
            <AccordionSummary
              expandIcon={
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              }
              sx={{
                borderRadius: "4px",
                border: "1px solid #9EE5F4",
                backgroundColor: "#FFF",
                boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CfrRegulationsIcon />
                <AccordionTitle className="accordion-title">
                  Applicable CFR Regulations ({complianceData.sections.cfrRegulationSections.total_sections} sections)
                </AccordionTitle>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "16px" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {complianceData.sections.cfrRegulationSections.parts.map((part) => {
                  const partKey = `${part.cfr_title}-${part.part_number}`;
                  return (
                    <Accordion
                      key={partKey}
                      expanded={expandedCfrPart === partKey}
                      onChange={handleCfrPartChange(partKey)}
                      disableGutters
                      elevation={0}
                      sx={{
                        borderRadius: "8px !important",
                        border: "1px solid #cff7ff",
                        backgroundColor: "#f8fdfe",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M5 7.5L10 12.5L15 7.5" stroke="#065b70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        }
                        sx={{
                          borderRadius: "8px",
                          minHeight: "48px",
                          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                            transform: "rotate(180deg)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            {part.cfr_title} CFR Part {part.part_number}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#374151",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            — {part.part_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#6b7280",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            ({part.group_short_name}) · {part.sections.length} section{part.sections.length !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          {[...part.sections].sort((a, b) =>
                            compareCfrSectionIds(
                              a.section_identifier,
                              b.section_identifier,
                            ),
                          ).map((s) => {
                            const sectionUrl = `https://www.ecfr.gov/current/title-${part.cfr_title}/section-${s.ecfr_identifier || s.section_identifier}`;
                            return (
                              <Box
                                key={s.section_identifier}
                                sx={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: "8px",
                                  padding: "6px 10px",
                                  borderRadius: "4px",
                                  "&:hover": { backgroundColor: "#e8f8fc" },
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    whiteSpace: "nowrap",
                                    fontFamily: "Rethink Sans, sans-serif",
                                    minWidth: "64px",
                                  }}
                                >
                                  § {s.section_identifier}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    color: "#374151",
                                    fontFamily: "Rethink Sans, sans-serif",
                                    flex: 1,
                                  }}
                                >
                                  {s.section_name}
                                </Typography>
                                <a
                                  href={sectionUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    fontSize: "11px",
                                    color: "#065b70",
                                    textDecoration: "none",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  eCFR ↗
                                </a>
                              </Box>
                            );
                          })}
                        </Box>
                        <Box sx={{ mt: 1, px: 1 }}>
                          <a
                            href={`https://www.ecfr.gov/current/title-${part.cfr_title}/part-${part.part_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: "12px", color: "#065b70", textDecoration: "none" }}
                          >
                            View full Part {part.part_number} on eCFR ↗
                          </a>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        )}

        {/* Federal Register / source-document sections from aggregator */}
        {complianceData.sections.sourceDocuments &&
          complianceData.sections.sourceDocuments.documents.length > 0 && (
          <StyledAccordion
            expanded={expanded === "sourceDocuments"}
            onChange={handleAccordionChange("sourceDocuments")}
          >
            <AccordionSummary
              expandIcon={
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              }
              sx={{
                borderRadius: "4px",
                border: "1px solid #9EE5F4",
                backgroundColor: "#FFF",
                boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CfrRegulationsIcon />
                <AccordionTitle className="accordion-title">
                  Federal Register Source Materials ({complianceData.sections.sourceDocuments.total_sections} sections)
                </AccordionTitle>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "16px" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {complianceData.sections.sourceDocuments.documents.map((doc) => {
                  const docKey = doc.document_number;
                  return (
                    <Accordion
                      key={docKey}
                      expanded={expandedSourceDocument === docKey}
                      onChange={handleSourceDocumentChange(docKey)}
                      disableGutters
                      elevation={0}
                      sx={{
                        borderRadius: "8px !important",
                        border: "1px solid #cff7ff",
                        backgroundColor: "#f8fdfe",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M5 7.5L10 12.5L15 7.5" stroke="#065b70" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        }
                        sx={{
                          borderRadius: "8px",
                          minHeight: "48px",
                          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                            transform: "rotate(180deg)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", flexWrap: "wrap" }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            {doc.citation || doc.document_number}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#374151",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            — {doc.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#6b7280",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            {doc.source_system || "Source"} · {doc.sections.length} section{doc.sections.length !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {doc.sections.map((section, index) => {
                            const headingPath = Array.isArray(section.heading_path)
                              ? section.heading_path.filter(Boolean).join(" / ")
                              : section.heading_path || "";
                            const text = section.excerpt || section.text || "";
                            return (
                              <Box
                                key={`${section.anchor_id || section.heading || index}`}
                                sx={{
                                  padding: "10px",
                                  borderRadius: "6px",
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #e8f8fc",
                                }}
                              >
                                <Box sx={{ display: "flex", gap: "8px", alignItems: "baseline", flexWrap: "wrap", mb: 0.75 }}>
                                  {section.anchor_id && (
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        color: "#065b70",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      #{section.anchor_id}
                                    </Typography>
                                  )}
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#374151",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {section.heading || headingPath || "Source section"}
                                  </Typography>
                                </Box>
                                {headingPath && section.heading !== headingPath && (
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      color: "#6b7280",
                                      fontFamily: "Rethink Sans, sans-serif",
                                      mb: 0.75,
                                    }}
                                  >
                                    {headingPath}
                                  </Typography>
                                )}
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    color: "#374151",
                                    fontFamily: "Rethink Sans, sans-serif",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {text || "No source text imported yet."}
                                </Typography>
                                {section.source_url && (
                                  <a
                                    href={section.source_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: "inline-block",
                                      marginTop: "8px",
                                      fontSize: "12px",
                                      color: "#065b70",
                                      textDecoration: "none",
                                    }}
                                  >
                                    Open source section ↗
                                  </a>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                        {doc.source_url && (
                          <Box sx={{ mt: 1, px: 1 }}>
                            <a
                              href={doc.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: "12px", color: "#065b70", textDecoration: "none" }}
                            >
                              View official document ↗
                            </a>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        )}

        {/* Audit Ready Checklist — always visible; backend may omit empty checklist */}
        <StyledAccordion
          expanded={expanded === "auditReadyChecklist"}
          onChange={handleAccordionChange("auditReadyChecklist")}
        >
          <AccordionSummary
            expandIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            sx={{
              borderRadius: "4px",
              border: "1px solid #9EE5F4",
              backgroundColor: "#FFF",
              boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
              "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                transform: "rotate(180deg)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                minWidth: 0,
                pr: 0.5,
              }}
            >
              <CheckListIcon />
              <AccordionTitle
                className="accordion-title"
                sx={{ flex: 1, minWidth: 0 }}
              >
                Give me an audit ready checklist
              </AccordionTitle>
              <Box
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <GlobalAdminLlmPromptButton
                  dialogTitle="Audit checklist — LLM prompt"
                  promptText={complianceData.llmPromptsBySection?.audit}
                  wallTimeSec={complianceData.llmInvokeWallSecondsBySection?.audit}
                />
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              backgroundColor: "#ffffff",
              // border: "2px solid #35d7e4",
              // borderRadius: "4px",
              padding: "12px",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {complianceData.sections.auditReadyChecklist &&
                complianceData.sections.auditReadyChecklist.length > 0 && (
                  <AuditChecklistTable
                    items={complianceData.sections.auditReadyChecklist}
                  />
                )}
            </Box>
          </AccordionDetails>
        </StyledAccordion>
      </Box>
    </Box>
  );
};

export default FacilityAnalysis;
